"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { API_BASE_URL } from "@/lib/site";

type TitleSummary = {
  slug: string;
  job_title: string;
  primary_domain_name: string | null;
};

export default function ResumeSearch({
  variant = "pill",
  popularSearches,
}: {
  variant?: "pill" | "bar";
  popularSearches?: { slug: string; label: string }[];
}) {
  const [query, setQuery] = useState("");
  const [titles, setTitles] = useState<TitleSummary[]>([]);
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/resumes/slugs`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setTitles(data.titles);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const results =
    query.trim().length > 0
      ? titles
          .filter((t) => t.job_title.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 8)
      : [];

  function goTo(slug: string) {
    setOpen(false);
    setQuery("");
    router.push(`/resumes/${slug}`);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (results.length) goTo(results[0].slug);
  }

  const dropdown =
    open && (results.length > 0 || query.trim().length > 0) ? (
      <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-[#0f172a]">
        {results.length ? (
          results.map((t) => (
            <button
              key={t.slug}
              type="button"
              onClick={() => goTo(t.slug)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm transition hover:bg-violet-50 dark:hover:bg-violet-500/10"
            >
              <span className="font-medium text-slate-900 dark:text-white">
                {t.job_title}
              </span>
              {t.primary_domain_name ? (
                <span className="flex-shrink-0 text-xs text-slate-400 dark:text-slate-500">
                  {t.primary_domain_name}
                </span>
              ) : null}
            </button>
          ))
        ) : (
          <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
            No job titles found for &ldquo;{query}&rdquo;
          </div>
        )}
      </div>
    ) : null;

  if (variant === "bar") {
    return (
      <div
        ref={containerRef}
        className="relative flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm lg:flex-row lg:items-center lg:gap-4 dark:border-slate-800 dark:bg-[#0f172a]"
      >
        <form onSubmit={handleSubmit} className="flex items-center gap-2 lg:w-[360px] lg:flex-shrink-0">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 focus-within:border-violet-400 dark:border-slate-700">
            <svg
              className="flex-shrink-0 text-slate-400"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder="Search for resume examples by job title or keyword..."
              className="w-full flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none dark:text-slate-200"
            />
          </div>
          <button
            type="submit"
            className="flex-shrink-0 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:brightness-110"
          >
            Search
          </button>
        </form>

        {popularSearches?.length ? (
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs lg:justify-end">
            <span className="flex-shrink-0 font-semibold text-slate-700 dark:text-slate-300">Popular Searches:</span>
            {popularSearches.map((p) => (
              <Link
                key={p.slug}
                href={`/resumes/${p.slug}`}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {p.label}
              </Link>
            ))}
          </div>
        ) : null}

        {dropdown}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative mx-auto w-full max-w-xl">
      <div className="relative">
        <svg
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search job titles — e.g. Software Engineer, Nurse, Accountant…"
          className="w-full rounded-full border border-slate-200/80 bg-white/90 py-3.5 pl-12 pr-4 text-sm text-slate-700 shadow-sm backdrop-blur placeholder:text-slate-400 focus:border-violet-400 focus:outline-none dark:border-slate-700/80 dark:bg-[#0f172a]/90 dark:text-slate-200"
        />
      </div>

      {dropdown}
    </div>
  );
}
